import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { selectToken, selectUserCountry } from 'store/auth/selectors';
import DateTime from 'utils/datetime';
import { getRoleFromToken } from 'utils/helpers';

import type { RangePickerValue } from 'components/DateRange';
import type { Dayjs } from 'dayjs';
import type { TaskStatus, TaskType } from 'types/common';

export type TaskManagementFilters = {
  country: string | null;
  dateRange?: RangePickerValue;
  plantingGroupName?: string | null;
  taskStatus?: TaskStatus[] | null;
  taskType?: TaskType[] | null;
  agronomist?: string[] | null;
  manager?: string[] | null;
  isTodaysTasks?: boolean;
};

const getDefaultDateRange = () =>
  [
    DateTime.startOfWeek(DateTime.now()),
    DateTime.endOfWeek(DateTime.now()),
  ] as RangePickerValue;

const initialTaskManagementFilterState: TaskManagementFilters = {
  country: null,
  plantingGroupName: null,
  taskStatus: null,
  taskType: null,
  agronomist: null,
  manager: null,
  isTodaysTasks: false,
  dateRange: getDefaultDateRange(),
};

const parseFilters = (
  params: URLSearchParams,
  defaultCountry: string | null,
): TaskManagementFilters => {
  const dateRangeParam = params.get('dateRange');
  let dateRange: RangePickerValue | null = null;

  if (dateRangeParam) {
    const [start, end] = dateRangeParam.split('_');
    if (start && end) {
      dateRange = [
        DateTime.toDayjs(start),
        DateTime.toDayjs(end),
      ] as RangePickerValue;
    }
  }

  return {
    ...initialTaskManagementFilterState,
    country: params.get('country') ?? defaultCountry,
    plantingGroupName: params.get('plantingGroupName'),
    taskStatus: params.get('taskStatus')
      ? (params.get('taskStatus')?.split(',') as TaskStatus[])
      : null,
    taskType: params.get('taskType')
      ? (params.get('taskType')?.split(',') as TaskType[])
      : null,
    agronomist: params.get('agronomist')
      ? params.get('agronomist')?.split(',')
      : null,
    manager: params.get('manager') ? params.get('manager')?.split(',') : null,
    isTodaysTasks: params.get('isTodaysTasks') === '1',
    dateRange: dateRange ?? getDefaultDateRange(),
  };
};

const encodeFilters = (
  filters: TaskManagementFilters,
): Record<string, string> => {
  const params: Record<string, string> = {};

  if (filters.country) params.country = filters.country;
  if (filters.plantingGroupName)
    params.plantingGroupName = filters.plantingGroupName;
  if (filters.taskStatus?.length)
    params.taskStatus = filters.taskStatus.join(',');
  if (filters.taskType?.length) params.taskType = filters.taskType.join(',');
  if (filters.agronomist?.length)
    params.agronomist = filters.agronomist.join(',');
  if (filters.manager?.length) params.manager = filters.manager.join(',');
  if (filters.isTodaysTasks) params.isTodaysTasks = '1';
  if (filters.dateRange?.[0] && filters.dateRange?.[1]) {
    params.dateRange = `${filters.dateRange[0].toISOString()}_${filters.dateRange[1].toISOString()}`;
  }

  return params;
};

const managerRoleSet = new Set(['PM', 'GM', 'Area Manager', 'Admin']);

const useTaskManagementFilters = () => {
  const userCountry = useSelector(selectUserCountry);
  const [searchParams, setSearchParams] = useSearchParams();

  const userRole = getRoleFromToken(useSelector(selectToken));

  const isManagerRole = managerRoleSet.has(userRole);

  // Whenever searchParams or userCountry changes, parse the filters from the URL and store them in appropriate data structures
  const filters = useMemo(() => {
    const filters = parseFilters(searchParams, userCountry);
    if (!isManagerRole) {
      filters.manager = null;
      filters.country = userCountry;
    }
    return filters;
  }, [searchParams, userCountry, isManagerRole]);

  const setParams = (nextFilters: TaskManagementFilters) => {
    setSearchParams(encodeFilters(nextFilters), { replace: true });
  };

  const setCountry = (country: string | null) => {
    // Whenever country changes, reset other filters
    setParams({
      ...initialTaskManagementFilterState,
      country,
      dateRange: getDefaultDateRange(),
    });
  };

  const setPlantingGroupName = (name: string | null) => {
    setParams({ ...filters, plantingGroupName: name });
  };

  const setAgronomist = (agronomist: string[] | null) => {
    setParams({ ...filters, agronomist });
  };

  const setManager = (manager: string[] | null) => {
    setParams({ ...filters, manager });
  };

  const setTaskStatus = (status: TaskStatus[] | null) => {
    setParams({ ...filters, taskStatus: status });
  };

  const setTaskType = (type: TaskType[] | null) => {
    setParams({ ...filters, taskType: type });
  };

  const setTodaysTasksVisibility = (isTodaysTasks: boolean) => {
    setParams({
      ...filters,
      isTodaysTasks,
      dateRange: isTodaysTasks
        ? ([
            DateTime.startOfDay(DateTime.now()) as Dayjs,
            DateTime.endOfDay(DateTime.now()) as Dayjs,
          ] as RangePickerValue)
        : getDefaultDateRange(),
    });
  };

  const setDateRange = (dateRange: RangePickerValue | null) => {
    setParams({ ...filters, dateRange, isTodaysTasks: false });
  };

  const resetFilters = () => {
    setParams({
      ...initialTaskManagementFilterState,
      country: userCountry,
    });
  };

  const hasActiveFilters = useMemo(() => {
    let isActive = false;
    for (const [key, value] of Object.entries(filters)) {
      if (key === 'country') {
        continue;
      }

      if (Array.isArray(value)) {
        if (
          DateTime.isValid(value?.[0] as Dayjs) &&
          DateTime.isValid(value?.[1] as Dayjs)
        ) {
          const isThisWeek =
            DateTime.isSameWeek(value?.[0] as Dayjs, DateTime.now()) &&
            DateTime.isSameWeek(value?.[1] as Dayjs, DateTime.now());

          if (!isThisWeek) {
            isActive = true;
            break;
          }
          continue;
        }
        if (value.length > 0) {
          isActive = true;
          break;
        }
      }

      if (value !== null && value !== '' && value !== false) {
        isActive = true;
        break;
      }
    }

    return isActive;
  }, [filters]);

  return {
    filters,
    hasActiveFilters,
    setCountry,
    setPlantingGroupName,
    setAgronomist,
    setTaskStatus,
    setTaskType,
    setTodaysTasksVisibility,
    setDateRange,
    setManager,
    isManagerRole,
    resetFilters,
  };
};

export default useTaskManagementFilters;
